import {
  configApiRef,
  microsoftAuthApiRef,
  SessionState,
  useApi,
} from '@backstage/core-plugin-api';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { RiCheckLine, RiErrorWarningLine } from '@remixicon/react';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { microsoftChinaAuthApiRef } from './jumphost-utils';
import { AzureCloudPickerProps } from './schema';
import { useTheme } from '@mui/material/styles';
import { AzureCloud } from './types';

interface LoadingState {
  loading: boolean;
  msg: string;
  error?: boolean;
}

const SignedInLabel = ({ isSignedIn }: { isSignedIn: boolean }) => {
  const theme = useTheme();
  const { error, success } = theme.palette;

  return (
    <Typography color={isSignedIn ? success.main : error.main} fontSize={12}>
      {isSignedIn ? (
        <span>
          Signed In{' '}
          <RiCheckLine
            style={{
              verticalAlign: 'text-top',
              height: '14px',
              color: success.main,
            }}
          />
        </span>
      ) : (
        <span>
          Sign in first to select{' '}
          <RiErrorWarningLine
            style={{
              verticalAlign: 'text-top',
              height: '14px',
              color: error.main,
            }}
          />
        </span>
      )}
    </Typography>
  );
};

export const AzureCloudPicker = ({
  onChange,
  formData,
  uiSchema,
}: typeof AzureCloudPickerProps) => {
  const { secrets, setSecrets } = useTemplateSecrets();
  const msAuthApi = useApi(microsoftAuthApiRef);

  const msChinaAuthApi = useApi(microsoftChinaAuthApiRef);
  const configAPI = useApi(configApiRef);
  const apiScopesChina =
    configAPI.getOptionalStringArray(
      'xelerate.jumphost.additionalChinaScopes',
    ) ?? [];
  const secretsKey = uiSchema['ui:options']?.secretsKey!;
  const [loadingState, setLoadingState] = useState<LoadingState>({
    loading: false,
    msg: '',
  });

  function handleSignIn(cloud: AzureCloud) {
    setLoadingState({ loading: true, msg: 'Signing in...' });
    (cloud === AzureCloud.AzureChina ? msChinaAuthApi : msAuthApi)
      .signIn()
      .finally(() => setLoadingState({ loading: false, msg: '' }));
  }

  async function handleGetToken(cloud: AzureCloud) {
    setLoadingState({ loading: true, msg: 'Getting token...' });

    try {
      let token: string;

      if (cloud === AzureCloud.AzureGlobal) {
        const pureToken = await msAuthApi.getAccessToken();
        const { appid } = JSON.parse(atob(pureToken.split('.')[1]));

        token = await msAuthApi.getAccessToken([
          `api://${appid}/Xelerate.Users`,
        ]);
      } else {
        token = await msChinaAuthApi.getAccessToken(apiScopesChina);
      }

      setSecrets({
        ...secrets,
        cloudType: cloud,
        [secretsKey]: token,
      });
      setLoadingState({
        loading: false,
        msg: '',
        error: false,
      });
      onChange(cloud);
    } catch (e: any) {
      setLoadingState({
        loading: false,
        msg: `Failed to get ${cloud} accesstoken. ${e.message}`,
        error: true,
      });
    }
  }

  const [msAuthState, setMsAuthState] = useState<boolean>(false);
  const [msChinaAuthState, setMsChinaAuthState] = useState<boolean>(false);

  useEffect(() => {
    msAuthApi
      .sessionState$()
      .subscribe(v => setMsAuthState(v === SessionState.SignedIn));
    msChinaAuthApi
      .sessionState$()
      .subscribe(v => setMsChinaAuthState(v === SessionState.SignedIn));
  }, [msAuthApi, msChinaAuthApi]);

  return !secretsKey ? (
    <Backdrop open style={{ zIndex: 9999 }}>
      <Typography variant="h3" color="warning">
        Missing secretsKey in the uiSchema
      </Typography>
    </Backdrop>
  ) : (
    <section>
      <Stack spacing={2} direction="row" gap={4} justifyContent="center">
        {(
          [
            [msAuthState, AzureCloud.AzureGlobal],
            [msChinaAuthState, AzureCloud.AzureChina],
          ] as Array<[boolean, AzureCloud]>
        ).map(([state, cloud]) => (
          <Card key={cloud}>
            <CardContent>
              <SignedInLabel isSignedIn={state} />
              <Typography variant="h3">{cloud}</Typography>
            </CardContent>
            <CardActions style={{ columnGap: '2rem' }}>
              {!state && (
                <Button onClick={() => handleSignIn(cloud)}>Sign In</Button>
              )}
              <Button
                disabled={!state || formData === cloud}
                onClick={() => handleGetToken(cloud)}
                variant={formData === cloud ? 'contained' : 'outlined'}
              >
                {formData === cloud ? 'Selected' : 'Select'}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Stack>

      {loadingState.loading && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{ padding: '0.5rem' }}
        >
          <CircularProgress color="primary" />
          <Typography variant="body2" sx={{ fontWeight: 200 }} color="primary">
            {loadingState.msg}
          </Typography>
        </Stack>
      )}

      {loadingState.error && (
        <Typography variant="body2" color="error">
          {loadingState.msg}
        </Typography>
      )}

      <output style={{ textAlign: 'right' }}>
        {formData ? (
          <>
            <Typography variant="h6">Selected Cloud</Typography>
            <Typography variant="body1">{formData}</Typography>
          </>
        ) : (
          <Typography variant="body1">
            Please select a cloud
            <br />
            before you continue
          </Typography>
        )}
      </output>
    </section>
  );
};
